/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { sha256 } from 'js-sha256';
import { extend, isEmpty, merge } from 'lodash';
import { NvHttpEndpointFactoryService } from 'nv-angular-http-endpoint/lib/nv-http-endpoint-factory/nv-http-endpoint-factory.service';
import { NvHttpEndpoint } from 'nv-angular-http-endpoint/lib/nv-http-endpoint/nv-http-endpoint';
import { PROXY_CONFIG } from 'proxy-config/lib/proxy.config.provider';
import { ProxyConfigType } from 'proxy-config/lib/proxy.config';
import { GUEST_MODE_PROXY_CONFIG } from '../../../../../projects/gfn/shared/src/lib/models/deeplink.types';
import { GfcEventsServerConfig } from './../config/gfc-events.config';
import {
    FeedbackProtocolVersion,
    GfcOriginalServiceName,
    GfcServiceUseCase
} from './../constants/gfc-events.constants';

const gfcHeadUrl = 'gfc/v4.0/head';
const gfcAttachmentUrl = 'gfc/v4.0/attachment';

@Injectable({
    providedIn: 'root'
})
export class GfcEventsEndpointsService {
    private feedbackEndpointUrl: NvHttpEndpoint;
    private feedbackAttachmentEndpointUrl: NvHttpEndpoint;
    private xFeedbackId: string;
    private latestOffset = 0;
    private commonFeedbackEventData = {};
    private previousChunkDataSize = 0;
    private server: string;

    constructor(
        private serverConfig: GfcEventsServerConfig,
        private endpointFactory: NvHttpEndpointFactoryService,
        @Optional() @Inject(PROXY_CONFIG) proxyConfig: ProxyConfigType
    ) {
        this.prepareCommonFeedbackData(serverConfig);
        this.feedbackEndpointUrl = this.prepareFeedbackEndpoint();
        this.feedbackAttachmentEndpointUrl = this.prepareFeedbackAttachmentEndpoint();
        if (proxyConfig?.useProxy) {
            this.server = proxyConfig.proxyUrl;
        } else {
            this.server = this.serverConfig.server;
        }
    }

    private generateUrl(url: string) {
        return this.server + '/' + url;
    }

    private computeSHA256Checksum(data: string): string {
        let checksum = '';
        if (!isEmpty(data)) {
            checksum = sha256(data);
        }
        return checksum;
    }

    private getDataSize(data: string): number {
        return !isEmpty(data) ? new Blob([data]).size : 0;
    }

    private prepareFeedbackEndpoint(): NvHttpEndpoint {
        this.feedbackEndpointUrl = this.endpointFactory.createEndpoint({
            headers: {
                'Content-Type': 'application/json'
            },
            config: this.serverConfig.retryConfig,
            reportRequestData: {
                originalService: GfcOriginalServiceName,
                serviceUseCase: GfcServiceUseCase.GfcHeadRequest,
                reportSuccessTelemetry: 'false'
            }
        });
        return this.feedbackEndpointUrl;
    }

    private prepareFeedbackAttachmentEndpoint(): NvHttpEndpoint {
        this.feedbackAttachmentEndpointUrl = this.endpointFactory.createEndpoint({
            config: this.serverConfig.retryConfig,
            reportRequestData: {
                originalService: GfcOriginalServiceName,
                serviceUseCase: GfcServiceUseCase.GfcAttachmentRequest,
                reportSuccessTelemetry: 'false'
            }
        });
        return this.feedbackAttachmentEndpointUrl;
    }

    private prepareCommonFeedbackData(serverConfig: GfcEventsServerConfig) {
        this.commonFeedbackEventData = extend(this.commonFeedbackEventData, {
            clientId: serverConfig.clientId,
            feedbackProtocol: FeedbackProtocolVersion,
            feedbackSchemaVer: serverConfig.schemaVersion,
            feedbackSysVer: serverConfig.version
        });
    }

    private buildGFCMultipartData(
        data: string,
        feedbackId: string,
        dataSize: number,
        eventParameters?: any,
        offset: number = 0,
        eob: boolean = false
    ): FormData {
        // Content-Type: multipart/form-data
        const formData = new FormData();
        formData.append('data2', new Blob([data]));
        formData.append('feedbackId', feedbackId);
        formData.append('size', dataSize.toString());
        formData.append('checksum', this.computeSHA256Checksum(data));
        if (eventParameters) {
            formData.append('parameters', JSON.stringify(eventParameters.parameters));
        }
        formData.append('offset', offset.toString());
        if (eob) {
            formData.append('eob', 'true');
        }
        return formData;
    }

    private updatePreviousChunkSizeAndBuildMultipartData(
        data: string,
        eventParameters?: any,
        eob: boolean = false
    ): FormData {
        this.previousChunkDataSize = this.getDataSize(data);
        return this.buildGFCMultipartData(
            data,
            this.xFeedbackId,
            this.previousChunkDataSize,
            eventParameters,
            this.latestOffset,
            eob
        );
    }

    private sendFeedbackHeadRequest(eventData: any): Observable<object> {
        eventData = merge(eventData, this.commonFeedbackEventData);
        return this.feedbackEndpointUrl.makeRequestEx<object>('POST', this.generateUrl(gfcHeadUrl), {
            body: eventData,
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        });
    }

    sendFeedbackEvent(eventData: any): Observable<object> {
        // If a new feedback session is created while the previous one was not explicitly closed by client
        // then we need to reset the offset so that the new feedback request is initiated with the default
        // offset 0
        this.latestOffset = 0;
        this.xFeedbackId = eventData.feedbackId;
        return this.sendFeedbackHeadRequest(eventData);
    }

    sendFeedbackChunkEvent(
        data: string,
        eventParameters: any,
        isLastChunk = false,
        params?: { [key: string]: any }
    ): Observable<object> {
        if (!isLastChunk) {
            const formData = this.updatePreviousChunkSizeAndBuildMultipartData(data, eventParameters);
            return this.sendFeedbackAttachmentReq(formData);
        } else if (!params?.isBrowserClosing) {
            // This is not a browser tab closure scenario, but instead a case of closing feedback session
            // when we move back from streamer to mall
            // update the offset value only if previous chunk is in progress
            if (params.prevChunkInProgress) {
                this.updateOffsetOnSuccessfulAttachmentRequest();
            }
            const formData = this.updatePreviousChunkSizeAndBuildMultipartData(data, eventParameters, true);
            this.latestOffset = 0;
            return this.sendFeedbackAttachmentReq(formData);
        } else {
            this.sendLastFeedbackChunkEvent(data, eventParameters);
            return of({});
        }
    }

    private sendFeedbackAttachmentReq(formData: FormData): Observable<object> {
        return this.feedbackAttachmentEndpointUrl.makeRequestEx<object>('POST', this.generateUrl(gfcAttachmentUrl), {
            body: formData,
            withCredentials: true
        });
    }

    /**
     * For browser tab closure or window unload event scenario,
     * calling the HTTP post endpoint using navigator.sendBeacon() API.
     * This is because a regular xhr sync request is not guaranteed to get sent in this scenario.
     *
     * @param data log chunk to be sent
     * @param streamerSessionId ragnarok session Id
     */
    sendLastFeedbackChunkEvent(data: string, eventParameters: any) {
        const formData = this.updatePreviousChunkSizeAndBuildMultipartData(data, eventParameters, true);
        navigator.sendBeacon(this.generateUrl(gfcAttachmentUrl), formData);
        this.latestOffset = 0;
    }

    updateOffsetOnSuccessfulAttachmentRequest(forceOffset?: number) {
        // Update the latest offset
        if (forceOffset) {
            this.latestOffset = forceOffset;
        } else {
            this.latestOffset = Number(this.latestOffset) + Number(this.previousChunkDataSize);
        }
    }

    sendFeedbackBasedOnTechConsent(eventData: any, technicalConsent: boolean, attachmentData: string) {
        eventData.sentTs = new Date().toISOString();
        return this.sendFeedbackHeadRequest(eventData).pipe(
            mergeMap(() => {
                if (technicalConsent) {
                    const formData = this.buildGFCMultipartData(
                        attachmentData,
                        eventData.feedbackId,
                        this.getDataSize(attachmentData),
                        undefined,
                        0,
                        true
                    );
                    return this.sendFeedbackAttachmentReq(formData);
                }
                return of(true);
            })
        );
    }
}
